// Applying a move, with every capture hook and terrain rule inside, so the
// search and the game agree on what a move does.

import { CAPTURE_ENERGY_GAIN, UNNUMBERED_PIECE_ID } from '../constants';
import { ABILITY_NAMES, addAbilities, addLearnSources, BASE_ABILITIES } from './abilities';
import { describePiece, OWNER_NAMES } from './describe';
import { ABILITY_VALUE } from './evaluate';
import { isSide, withPiece } from './pieces';
import { pushTarget } from './push';
import { addEnergy } from './roles';
import { neighborSquares, promotionRank, rankOf, squareName } from './squares';
import { cloneSpot, deadlyCrossing, isClone, isDeadly, isSolid, linkedExit } from './terrain';
import type { Ability, Board, Move, MoveOutcome, Note, Piece, Position, Side } from './types';

const PROMOTED_KIND = 'queen';

/** A pawn that reaches the far rank becomes a queen and keeps every extra. */
export const promoteIfDue = (piece: Piece, square: number): Piece => {
  if (piece.kind !== 'pawn' || !isSide(piece.owner)) return piece;
  if (rankOf(square) !== promotionRank(piece.owner)) return piece;
  const extras = piece.abilities.filter((ability) => ability !== 'pawn');
  return {
    ...piece,
    kind: PROMOTED_KIND,
    abilities: addAbilities(BASE_ABILITIES.queen, extras),
  };
};

export const demote = (piece: Piece): Piece => ({
  ...piece,
  kind: 'pawn',
  abilities: BASE_ABILITIES.pawn,
  gained: [],
  learnedFrom: {},
});

/** Releases every piece held by the side that just lost a piece. */
const releaseHostages = (board: Board, loser: Side): { board: Board; freed: number[] } => {
  const freed: number[] = [];
  const next = board.map((piece, square) => {
    if (piece?.heldBy === loser) {
      freed.push(square);
      return { ...piece, heldBy: null };
    }
    return piece;
  });
  return { board: next, freed };
};

/** The ability of the taken piece worth the most that the captor lacks, or undefined. */
const bestMissingAbility = (
  taken: readonly Ability[],
  held: readonly Ability[],
): Ability | undefined =>
  taken
    .filter((ability) => !held.includes(ability))
    .reduce<Ability | undefined>(
      (best, ability) =>
        best === undefined || ABILITY_VALUE[ability] > ABILITY_VALUE[best] ? ability : best,
      undefined,
    );

export const applyMove = (position: Position, move: Move): MoveOutcome => {
  const { rules } = position;
  const mover = position.board[move.from];
  if (!mover) throw new Error(`No piece on square ${move.from}`);
  const occupant = position.board[move.to];
  const notes: Note[] = [];
  const captured: Piece[] = [];
  let board = position.board;

  // A piece that crosses a deadly square is gone there, and the rest of the
  // move does not happen: no push, no capture. Rob, 2026-10-15: "consumes any
  // piece that crosses it in the course of its move".
  const crossing = deadlyCrossing(rules, move.from, move.to);
  if (crossing !== null) {
    notes.push({
      caption: 'Deadly square',
      text: `${describePiece(mover, move.from)} crossed the deadly square on ${squareName(crossing)}. Gone.`,
      squares: [move.from, crossing],
    });
    return {
      board: withPiece(board, move.from, null),
      captured: [mover],
      bounced: false,
      pushed: false,
      clones: [],
      promoted: false,
      notes,
    };
  }

  // A push comes before every capture rule. The occupant goes one square on
  // and the mover takes an empty square, so no capture hook fires.
  const pushedTo = pushTarget(position, move);
  const pushedPieceLanded = occupant !== null && pushedTo !== null && !isDeadly(rules, pushedTo);
  if (occupant && pushedTo !== null) {
    board = withPiece(board, move.to, null);
    const pusher = describePiece(mover, move.from);
    const victim = describePiece(occupant, move.to);
    if (isDeadly(rules, pushedTo)) {
      captured.push(occupant);
      notes.push({
        caption: 'Pushed into a deadly square',
        text: `${pusher} pushed ${victim} into the deadly square on ${squareName(pushedTo)}. Gone.`,
        squares: [move.to, pushedTo],
      });
    } else {
      board = withPiece(board, pushedTo, promoteIfDue({ ...occupant, moved: true }, pushedTo));
      notes.push({
        caption: 'A piece is pushed',
        text: `${pusher} pushed ${victim} to ${squareName(pushedTo)}.`,
        squares: [move.to, pushedTo],
      });
    }
  }
  const pushed = pushedTo !== null;
  const target = pushed ? null : occupant;

  if (target && target.lives > 1) {
    board = withPiece(board, move.to, { ...target, lives: target.lives - 1 });
    board = withPiece(board, move.from, { ...mover, moved: true, randomNextMove: false });
    return { board, captured, bounced: true, promoted: false, pushed, clones: [], notes };
  }

  board = withPiece(board, move.from, null);
  let landing: Piece | null = promoteIfDue(
    { ...mover, moved: true, randomNextMove: false },
    move.to,
  );
  let at = move.to;

  if (target) {
    captured.push(target);
    if (target.killsCaptor) {
      notes.push({
        caption: 'The captor is removed',
        text: `${describePiece(target, move.to)} was poisoned. ${describePiece(mover, move.from)} died with it.`,
        squares: [move.to],
      });
      captured.push(mover);
      landing = null;
    }
    if (landing && target.demotesCaptor) {
      landing = demote(landing);
      notes.push({
        caption: 'The captor is demoted',
        text: `${describePiece(target, move.to)} demoted its captor. The captor is a pawn now.`,
        squares: [move.to],
      });
    }
    // Godsmode: every captor takes an ability. Garrett, 2026-10-15, so that a
    // capture changes what a piece can do.
    if (landing && (landing.stealsAbility || (rules.captureSpoils && isSide(target.owner)))) {
      const stolen = bestMissingAbility(target.abilities, landing.abilities);
      if (stolen) {
        landing = {
          ...landing,
          abilities: addAbilities(landing.abilities, [stolen]),
          gained: addAbilities(landing.gained, [stolen]),
          learnedFrom: addLearnSources(landing.learnedFrom, [stolen], {
            how: 'capture',
            kind: target.kind,
          }),
        };
        notes.push({
          caption: 'An ability is taken',
          text: `${describePiece(mover, move.from)} took ${ABILITY_NAMES[stolen]} from it.`,
          squares: [move.to],
        });
      }
    }
    if (target.passesAbilities) {
      const heirSquare = neighborSquares(move.to).find(
        (square) => board[square]?.owner === target.owner && square !== move.from,
      );
      const heir = heirSquare === undefined ? null : board[heirSquare];
      if (heir && heirSquare !== undefined) {
        const learned = target.abilities.filter((ability) => !heir.abilities.includes(ability));
        board = withPiece(board, heirSquare, {
          ...heir,
          abilities: addAbilities(heir.abilities, learned),
          gained: addAbilities(heir.gained, learned),
          learnedFrom: addLearnSources(heir.learnedFrom, learned, {
            how: 'inherit',
            kind: target.kind,
          }),
        });
        notes.push({
          caption: 'Abilities pass to a neighbor',
          text: `${describePiece(heir, heirSquare)} inherited everything from ${describePiece(target, move.to)}.`,
          squares: [heirSquare],
        });
      }
    }
    if (isSide(target.owner)) {
      const released = releaseHostages(board, target.owner);
      board = released.board;
      if (released.freed.length > 0) {
        notes.push({
          caption: 'A held piece is released',
          text: 'A held piece was released.',
          squares: released.freed,
        });
      }
    }
  }

  if (landing && isDeadly(rules, at)) {
    notes.push({
      caption: 'Deadly square',
      text: `${describePiece(mover, move.from)} stepped into deadly square. Gone.`,
      squares: [at],
    });
    captured.push(landing);
    landing = null;
  }

  if (landing) {
    const exit = linkedExit(rules, at);
    if (exit !== null && board[exit] === null && !isSolid(rules, exit)) {
      notes.push({
        caption: 'Through the link',
        text: `${describePiece(mover, move.from)} crossed the link to ${squareName(exit)}.`,
        squares: [at, exit],
      });
      at = exit;
    }
  }

  board = withPiece(board, move.to, null);
  if (landing) board = withPiece(board, at, landing);

  // A clone square copies each piece that landed on it: the mover, and a piece pushed there.
  const clones: number[] = [];
  const landedSquares = [
    ...(pushedPieceLanded && pushedTo !== null ? [pushedTo] : []),
    ...(landing ? [at] : []),
  ];
  for (const square of landedSquares) {
    const cloned = cloneOnSquare(board, rules, square);
    if (!cloned) continue;
    board = cloned.board;
    if (cloned.spot !== null) clones.push(cloned.spot);
    notes.push(cloned.note);
  }

  // A capture gives the energy the piece held to the side of the captor, as far as it has room.
  // In Godsmode a capture of a piece of a side gives `CAPTURE_ENERGY_GAIN` more.
  const spoilsEnergy =
    target && rules.captureSpoils && isSide(target.owner) ? CAPTURE_ENERGY_GAIN : 0;
  const capturedEnergy = target ? target.energy + spoilsEnergy : 0;
  if (target && capturedEnergy > 0 && isSide(mover.owner)) {
    const taken = addEnergy(board, mover.owner, capturedEnergy);
    board = taken.board;
    if (taken.stored > 0) {
      notes.push({
        caption: 'Energy is taken',
        text: `${OWNER_NAMES[mover.owner]} took ${taken.stored} energy from ${describePiece(target, move.to)}.`,
        squares: [move.to],
        energy: { side: mover.owner, count: taken.stored },
      });
    }
  }

  return {
    board,
    captured,
    bounced: false,
    pushed,
    clones,
    promoted: landing !== null && landing.kind === PROMOTED_KIND && mover.kind !== PROMOTED_KIND,
    notes,
  };
};

/**
 * What a clone square does to the piece that landed on it. Null when the
 * square is not a clone square or holds no piece. The spot is null when no
 * square beside it is empty. The copy has no id. The caller gives it one.
 */
export const cloneOnSquare = (
  board: Board,
  rules: Position['rules'],
  square: number,
): { board: Board; spot: number | null; note: Note } | null => {
  const original = board[square];
  if (!original || !isClone(rules, square)) return null;
  const who = describePiece(original, square);
  const spot = cloneSpot(board, rules, square, original.id);
  if (spot === null) {
    return {
      board,
      spot,
      note: {
        caption: 'No room for a copy',
        text: `${who} landed on the clone square. No empty square beside it, so no copy.`,
        squares: [square],
      },
    };
  }
  const copy: Piece = { ...original, id: UNNUMBERED_PIECE_ID, home: spot, movesTwice: false };
  return {
    board: withPiece(board, spot, copy),
    spot,
    note: {
      caption: 'A piece is cloned',
      text: `${who} landed on the clone square. A copy of it stands on ${squareName(spot)}.`,
      squares: [square, spot],
    },
  };
};

/** Removes the piece and its eight neighbors. */
export const detonate = (
  board: Board,
  square: number,
): { board: Board; captured: Piece[]; squares: number[] } => {
  const squares = [square, ...neighborSquares(square)];
  const captured: Piece[] = [];
  const next = [...board];
  for (const s of squares) {
    const piece = next[s];
    if (piece) captured.push(piece);
    next[s] = null;
  }
  return { board: next, captured, squares };
};

export const countRoyals = (board: Board, side: Side): number =>
  board.reduce((count, piece) => (piece?.owner === side && piece.royal ? count + 1 : count), 0);

/** The pieces of both sides on the board. */
export const countSidePieces = (board: Board): number =>
  board.reduce((count, piece) => (piece && isSide(piece.owner) ? count + 1 : count), 0);

/** A blast or a vanishing file can take both last royals at once. */
export const bothCrownsFallen = (board: Board): boolean =>
  countRoyals(board, 'gold') === 0 && countRoyals(board, 'violet') === 0;

/** The side that still has a royal piece when the other has none. */
export const winnerOf = (board: Board): Side | null => {
  const goldRoyals = countRoyals(board, 'gold');
  const violetRoyals = countRoyals(board, 'violet');
  if (goldRoyals === 0 && violetRoyals > 0) return 'violet';
  if (violetRoyals === 0 && goldRoyals > 0) return 'gold';
  return null;
};
